// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import {
  useDeleteEligibilityConnection,
  useEligibilityConnection,
  useUpsertEligibilityConnection,
  type EligibilityConnectionView,
} from '../../api/eligibility-connection.js';

type ProviderKind = EligibilityConnectionView['providerKind'];

const PROVIDER_KINDS: { value: ProviderKind; label: string; needsCreds: boolean }[] = [
  { value: 'fixture', label: 'Fixture (dev / no real check)', needsCreds: false },
  { value: 'changehealthcare', label: 'Change Healthcare', needsCreds: true },
  { value: 'availity', label: 'Availity', needsCreds: true },
];

/**
 * Admin-only settings panel for the org's eligibility clearinghouse
 * connection (bead 3qn). Mirrors EmrConnectionPanel from 8de —
 * credentials are write-only on the API, picker has a "no real
 * check yet" fixture option for dev, real providers persist their
 * config + creds so they're ready when bead xe5 lands.
 */
export function EligibilityConnectionPanel() {
  const { data, error: loadError } = useEligibilityConnection();
  const upsert = useUpsertEligibilityConnection();
  const remove = useDeleteEligibilityConnection();

  const [kind, setKind] = useState<ProviderKind>('fixture');
  const [displayName, setDisplayName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (data?.connection === null || data?.connection === undefined) return;
    setKind(data.connection.providerKind);
    setDisplayName(data.connection.displayName ?? '');
    setPartnerId(data.connection.partnerId ?? '');
    // Never preload the api key — even if the API returned it,
    // showing it in the input would defeat the write-only model.
    setApiKey('');
  }, [data]);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: Parameters<typeof upsert.mutate>[0] = {
      providerKind: kind,
      displayName: displayName.trim().length > 0 ? displayName.trim() : null,
      partnerId: partnerId.trim().length > 0 ? partnerId.trim() : null,
    };
    if (apiKey.trim().length > 0) {
      body.credentials = { apiKey: apiKey.trim() };
    }
    upsert.mutate(body, {
      onSuccess: () => {
        setApiKey('');
      },
    });
  };

  const onDisconnect = (): void => {
    remove.mutate(undefined, {
      onSuccess: () => {
        setKind('fixture');
        setDisplayName('');
        setPartnerId('');
        setApiKey('');
      },
    });
  };

  const current = data?.connection;
  const kindInfo = PROVIDER_KINDS.find((k) => k.value === kind) ?? {
    value: 'fixture' as const,
    label: '',
    needsCreds: false,
  };
  const credentialsNeeded = kindInfo.needsCreds;
  const credentialsExist = current !== null && current?.hasCredentials === true;

  return (
    <Card
      heading="Insurance eligibility provider"
      variant="default"
      data-eligibility-connection-panel
    >
      <p className="text-sm text-muted-foreground" style={{ marginBottom: 12 }}>
        Configure the clearinghouse this clinic uses for real-time eligibility checks at intake
        submission. The fixture provider returns canned results for dev / demo; real providers need
        a partner id and an API key (encrypted at rest with the server's master key).
      </p>

      {loadError !== null && loadError !== undefined ? (
        <div className="error-banner text-sm" role="alert" style={{ marginBottom: 12 }}>
          {loadError.message}
        </div>
      ) : null}

      <form onSubmit={onSubmit} style={{ maxWidth: 560 }}>
        <Stack gap={16}>
          <Stack gap={4}>
            <Label htmlFor="elig-kind">Provider</Label>
            <select
              id="elig-kind"
              data-eligibility-kind
              value={kind}
              onChange={(e) => setKind(e.currentTarget.value as ProviderKind)}
              className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {PROVIDER_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Stack>

          <Stack gap={4}>
            <Label htmlFor="elig-display-name">Display name (optional)</Label>
            <Input
              id="elig-display-name"
              type="text"
              value={displayName}
              data-eligibility-display-name
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              placeholder="CHC Prod, Availity Sandbox, etc."
            />
          </Stack>

          {credentialsNeeded ? (
            <>
              <Stack gap={4}>
                <Label htmlFor="elig-partner-id" required>
                  Partner / trading-partner id
                </Label>
                <Input
                  id="elig-partner-id"
                  type="text"
                  required
                  value={partnerId}
                  data-eligibility-partner-id
                  onChange={(e) => setPartnerId(e.currentTarget.value)}
                  placeholder="Clearinghouse-assigned id"
                />
              </Stack>
              <Stack gap={4}>
                <Label htmlFor="elig-api-key" required={!credentialsExist}>
                  {credentialsExist ? 'Rotate API key (optional)' : 'API key'}
                </Label>
                <Input
                  id="elig-api-key"
                  type="password"
                  value={apiKey}
                  data-eligibility-api-key
                  autoComplete="off"
                  onChange={(e) => setApiKey(e.currentTarget.value)}
                  placeholder={
                    credentialsExist ? 'Leave blank to keep the existing key' : 'API key (8+ chars)'
                  }
                />
                {credentialsExist ? (
                  <span className="text-xs text-muted-foreground">
                    A key is already stored. Submit a new value here to rotate it.
                  </span>
                ) : null}
              </Stack>
            </>
          ) : null}

          {upsert.error !== null && upsert.error !== undefined ? (
            <div className="error-banner text-sm" role="alert" aria-live="polite">
              {upsert.error.message}
            </div>
          ) : null}

          {upsert.isSuccess ? (
            <div role="status" aria-live="polite" className="text-sm text-muted-foreground">
              Connection saved.
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="submit" variant="primary" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save connection'}
            </Button>
            {current !== null && current !== undefined ? (
              <Button
                type="button"
                variant="danger"
                disabled={remove.isPending}
                data-eligibility-disconnect
                onClick={onDisconnect}
              >
                {remove.isPending ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            ) : null}
          </div>
        </Stack>
      </form>
    </Card>
  );
}
